import {
  CSSProperties,
  memo,
  ReactElement,
  ReactNode,
  SyntheticEvent,
  useEffect,
  useRef,
  useState
} from 'react';
import cc from 'classcat';
import { ZINDEX_HIGH_1 } from 'utils/constants/app/ui';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { isMobile } from 'utils/is-mobile';
import { rand } from 'utils/swarm/rid-generator';
import { Portal } from 'components/Shared/Portal';
import './index.less';

type TModalProps = {
  visible: boolean;
  title?: ReactElement | string;
  onCancel?: (e?: SyntheticEvent) => void;
  width?: string | number;
  wrapClassName: string;
  className?: string;
  centered?: boolean;
  zIndex?: number;
  style?: CSSProperties;
  bodyStyle?: CSSProperties;
  maskStyle?: CSSProperties;
  maskClosable?: boolean;
  mask?: boolean;
  footer?: ReactElement | null;
  closeIcon?: ReactNode;
  renderCloseIconInMask?: boolean;
  destroyOnClose?: boolean;
  closable?: boolean;
  afterClose?: () => void;
  children: ReactNode;
  contentPadding?: number | string;
  maxWidth?: string | number;
  hideBorder?: boolean;
  getContainer?: string | false;
  wrapperPadding?: string;
  fullPage?: boolean;
  placement?: 'top' | 'bottom' | 'left' | 'right' | undefined;
  borderRadius?: string;
  closeIconStyle?: CSSProperties;
};

if (SpringConfigs.IS_RTL) {
  import('./rtl.less');
}

const AFTER_CLOSE_DELAY = 300;
const MODAL_WRAPPER_PADDING = 24;
const MODAL_WRAPPER_MOBILE_PADDING = 12;
const randomNumberForAddBlocker = rand();

export const CustomModal = memo(
  ({
    visible,
    title,
    onCancel,
    width,
    wrapClassName,
    zIndex,
    style,
    bodyStyle,
    maskStyle,
    maskClosable = true,
    mask = true,
    footer,
    closeIcon,
    destroyOnClose,
    closable = true,
    afterClose,
    className,
    contentPadding,
    maxWidth,
    hideBorder = true,
    getContainer = false,
    centered = true,
    wrapperPadding,
    fullPage,
    placement,
    borderRadius,
    closeIconStyle,
    renderCloseIconInMask = false,
    children,
    ...rest
  }: TModalProps) => {
    const [modalVisible, setModalVisible] = useState(visible);
    const modalContainerRef = useRef<string | false>(getContainer);

    useEffect(() => {
      setModalVisible(visible);
      modalContainerRef.current = getContainer || false;
    }, [visible, getContainer]);

    const handleClose = () => {
      if (onCancel) {
        onCancel();
      } else {
        closeModal();
      }
    };

    const closeModal = () => {
      if (destroyOnClose) {
        setModalVisible(false);
      }

      if (afterClose && typeof afterClose === 'function') {
        const timeout = setTimeout(() => {
          afterClose();
          clearTimeout(timeout);
        }, AFTER_CLOSE_DELAY);
      }
    };

    const handleMaskClick = () => {
      if (maskClosable) {
        handleClose();
      }
    };

    const modalContent = (
      <>
        <div
          {...rest}
          className={cc([
            'customModal',
            {
              visible: modalVisible,
              customModal__notCentered: !centered,
              customModal__left: placement === 'left',
              customModal__right: placement === 'right',
              customModal__top: placement === 'top',
              customModal__bottom: placement === 'bottom'
            },
            `${randomNumberForAddBlocker}`
          ])}
          style={{ zIndex: zIndex ? zIndex : ZINDEX_HIGH_1, ...style }}
        >
          <div
            className={cc([
              'custom__modalContent',
              {
                [`${wrapClassName}`]: wrapClassName,
                'custom__modalContent--mobile': isMobile()
              }
            ])}
            style={{
              width,
              ...bodyStyle,
              padding: `${contentPadding}px`,
              maxWidth: `${maxWidth}${typeof maxWidth === 'string' ? '' : 'px'}`
            }}
          >
            <div
              className={className}
              style={{
                padding: wrapperPadding
                  ? wrapperPadding
                  : !isMobile()
                  ? MODAL_WRAPPER_PADDING
                  : MODAL_WRAPPER_MOBILE_PADDING,
                border: `${
                  hideBorder ? 'none' : '1px solid var(--v3-black-6)'
                }`,
                height: `${fullPage ? '100%' : 'unset'}`,
                borderRadius: borderRadius
                  ? borderRadius
                  : 'var(--v3-border-radius-s)'
              }}
            >
              {!renderCloseIconInMask && closable && (
                <span
                  style={closeIconStyle}
                  className="custom__modalClose"
                  onClick={handleClose}
                >
                  {closeIcon}
                </span>
              )}
              {!!title && (
                <div className="custom__modalHeader">
                  <span>{title}</span>
                </div>
              )}
              <div className="custom__modalBody">{children}</div>
              {footer}
            </div>
          </div>

          {renderCloseIconInMask && closable && (
            <span
              style={closeIconStyle}
              className="custom__modalClose"
              onClick={handleClose}
            >
              {closeIcon}
            </span>
          )}
        </div>

        {mask && (
          <div
            className={cc([
              'custom__modalMask',
              `${randomNumberForAddBlocker}`
            ])}
            style={maskStyle}
            onClick={handleMaskClick}
          />
        )}
      </>
    );

    return modalVisible ? (
      <Portal rootId={modalContainerRef.current || ''}>{modalContent}</Portal>
    ) : null;
  }
);
